import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getChatHistory,
  postChatMessage,
  postRating,
  postResetSession,
} from "services/chatbot";
import { v4 as uuidv4 } from "uuid";
import {
  createNewSessionId,
  getOrCreateSessionId,
  markSessionJustReset,
} from "utils/chatSession";

export const emptyPreFilters = {
  customers: [],
  industries: [],
  years: [],
  organizational_units: [],
  countries: [],
  document_types: [],
  categories: [],
  tags: [],
};

const initialState = {
  sessionId: null,
  messages: [],
  preFilters: { ...emptyPreFilters },
  suggestedFilters: {},
  status: "idle", // idle | loading | error
  error: null,
  isFilterPanelOpen: false,
  ratingTarget: null, // entryId currently open in RatingCommentModal, or null
  hasReset: false,
  historyLoaded: false,
};

// Maps a /api/v1/chat response into this slice's assistant-message shape.
// `question` is kept on the message so "Give me more"/re-apply-filters can
// resend the same input.
const mapAssistantMessage = (data, question) => ({
  id: data.entry_id || uuidv4(),
  role: "assistant",
  answer: data.answer,
  documents: data.documents || [],
  documentsFound: data.documents_found,
  pagination: data.pagination,
  appliedFilters: data.applied_filters,
  suggestedFilters: data.suggested_filters,
  isTileList: data.is_tile_list,
  questionCategory: data.question_category,
  entryId: data.entry_id,
  question,
  rating: null,
  createdAt: Date.now(),
});

// Best-effort mapping of a persisted GET /history turn into a user+assistant
// message pair. The exact turn shape isn't confirmed against a live backend
// yet (see ChatPanel/README.md open items) - this defensively reads a few
// plausible field names.
const historyTurnToMessages = turn => {
  const question = turn.search_query || turn.query || turn.input || "";
  const createdAt = turn.created_at
    ? new Date(turn.created_at).getTime()
    : Date.now();
  const entryId = turn._id || turn.entry_id;
  return [
    {
      id: uuidv4(),
      role: "user",
      text: question,
      createdAt,
    },
    {
      id: entryId || uuidv4(),
      role: "assistant",
      answer: turn.answer,
      documents: turn.documents || [],
      documentsFound: (turn.documents || []).length,
      pagination: turn.pagination,
      appliedFilters: turn.applied_filters,
      suggestedFilters: turn.suggested_filters,
      isTileList: turn.is_tile_list,
      questionCategory: turn.question_category,
      entryId,
      question,
      rating: turn.rating
        ? { rating: turn.rating.rating, comment: turn.rating.comment }
        : null,
      createdAt,
    },
  ];
};

export const SEND_CHAT_MESSAGE = createAsyncThunk(
  "@@CHAT/SEND_CHAT_MESSAGE",
  async (input, thunkAPI) => {
    const { chat } = thunkAPI.getState();
    const sessionId = chat.sessionId || getOrCreateSessionId();
    const response = await postChatMessage({
      input,
      preFilters: chat.preFilters,
      sessionId,
    });
    return { input, data: response.data };
  }
);

export const LOAD_MORE_RESULTS = createAsyncThunk(
  "@@CHAT/LOAD_MORE_RESULTS",
  async (messageId, thunkAPI) => {
    const { chat } = thunkAPI.getState();
    const message = chat.messages.find(m => m.id === messageId);
    const pageSize =
      message?.pagination?.page_size || message?.documents?.length || 10;
    const nextOffset = (message?.pagination?.offset || 0) + pageSize;
    const response = await postChatMessage({
      input: message?.question,
      preFilters: chat.preFilters,
      offset: nextOffset,
      sessionId: chat.sessionId,
    });
    return { messageId, data: response.data };
  }
);

// Re-sends the last question with new filters, satisfying "apply filters as
// a follow-up mid-conversation". If there's no prior question yet (filters
// applied before the first send), it just stores the filters for the next
// SEND_CHAT_MESSAGE.
export const APPLY_CHAT_FILTERS = createAsyncThunk(
  "@@CHAT/APPLY_CHAT_FILTERS",
  async (newFilters, thunkAPI) => {
    const { chat } = thunkAPI.getState();
    const lastUserMessage = [...chat.messages]
      .reverse()
      .find(m => m.role === "user");
    if (!lastUserMessage) {
      return { newFilters, input: null, data: null };
    }
    const response = await postChatMessage({
      input: lastUserMessage.text,
      preFilters: newFilters,
      sessionId: chat.sessionId,
    });
    return { newFilters, input: lastUserMessage.text, data: response.data };
  }
);

// Only clears the visible thread on a *successful* server response - never
// a client-only clear, per the reset requirement.
export const RESET_CHAT_SESSION = createAsyncThunk(
  "@@CHAT/RESET_CHAT_SESSION",
  async (_, thunkAPI) => {
    const { chat } = thunkAPI.getState();
    await postResetSession({ sessionId: chat.sessionId });
    const newSessionId = createNewSessionId();
    markSessionJustReset();
    return { newSessionId };
  }
);

export const LOAD_CHAT_HISTORY = createAsyncThunk(
  "@@CHAT/LOAD_CHAT_HISTORY",
  async () => {
    const sessionId = getOrCreateSessionId();
    try {
      const response = await getChatHistory({ sessionId });
      return { sessionId, data: response.data };
    } catch (error) {
      // A 404 here just means this session has no persisted history yet
      // (e.g. its very first run) - that's an expected empty state, not
      // an error to surface to the user.
      if (error.response?.status === 404) {
        return { sessionId, data: [] };
      }
      throw error;
    }
  }
);

export const SUBMIT_RATING = createAsyncThunk(
  "@@CHAT/SUBMIT_RATING",
  async ({ entryId, rating, comment }) => {
    await postRating({ entryId, rating, comment });
    return { entryId, rating, comment };
  }
);

const chat = createSlice({
  name: "@@CHAT",
  initialState,
  reducers: {
    OPEN_RATING_MODAL: (state, action) => {
      state.ratingTarget = action.payload;
    },
    CLOSE_RATING_MODAL: state => {
      state.ratingTarget = null;
    },
    TOGGLE_FILTER_PANEL: state => {
      state.isFilterPanelOpen = !state.isFilterPanelOpen;
    },
    SET_FILTER_PANEL_OPEN: (state, action) => {
      state.isFilterPanelOpen = action.payload;
    },
  },
  extraReducers: {
    [SEND_CHAT_MESSAGE.pending]: state => {
      state.status = "loading";
      state.error = null;
    },
    [SEND_CHAT_MESSAGE.fulfilled]: (state, action) => {
      const { input, data } = action.payload;
      state.status = "idle";
      state.sessionId = data.session_id || state.sessionId;
      state.messages.push({
        id: uuidv4(),
        role: "user",
        text: input,
        createdAt: Date.now(),
      });
      state.messages.push(mapAssistantMessage(data, input));
      if (data.suggested_filters) state.suggestedFilters = data.suggested_filters;
      state.hasReset = false;
    },
    [SEND_CHAT_MESSAGE.rejected]: (state, action) => {
      state.status = "error";
      state.error = action.error.message;
    },
    [LOAD_MORE_RESULTS.pending]: state => {
      state.status = "loading";
    },
    [LOAD_MORE_RESULTS.fulfilled]: (state, action) => {
      const { messageId, data } = action.payload;
      state.status = "idle";
      const message = state.messages.find(m => m.id === messageId);
      if (message) {
        message.documents = [...(message.documents || []), ...(data.documents || [])];
        message.pagination = data.pagination;
        message.documentsFound = data.documents_found;
      }
    },
    [LOAD_MORE_RESULTS.rejected]: (state, action) => {
      state.status = "error";
      state.error = action.error.message;
    },
    [APPLY_CHAT_FILTERS.pending]: state => {
      state.status = "loading";
    },
    [APPLY_CHAT_FILTERS.fulfilled]: (state, action) => {
      const { newFilters, input, data } = action.payload;
      state.status = "idle";
      state.preFilters = newFilters;
      if (data) {
        state.messages.push({
          id: uuidv4(),
          role: "user",
          text: input,
          isFilterReapply: true,
          createdAt: Date.now(),
        });
        state.messages.push(mapAssistantMessage(data, input));
        state.sessionId = data.session_id || state.sessionId;
      }
    },
    [APPLY_CHAT_FILTERS.rejected]: (state, action) => {
      state.status = "error";
      state.error = action.error.message;
    },
    [RESET_CHAT_SESSION.pending]: state => {
      state.status = "loading";
    },
    [RESET_CHAT_SESSION.fulfilled]: (state, action) => {
      state.sessionId = action.payload.newSessionId;
      state.messages = [];
      state.preFilters = { ...emptyPreFilters };
      state.suggestedFilters = {};
      state.hasReset = true;
      state.status = "idle";
      state.error = null;
    },
    [RESET_CHAT_SESSION.rejected]: (state, action) => {
      // Intentionally does NOT touch messages/sessionId - reset must only
      // ever take effect after a successful server response.
      state.status = "error";
      state.error = action.error.message || "Failed to reset chat session";
    },
    [LOAD_CHAT_HISTORY.fulfilled]: (state, action) => {
      const { sessionId, data } = action.payload;
      state.sessionId = sessionId;
      const turns = Array.isArray(data) ? data : data?.history || [];
      state.messages = turns.flatMap(historyTurnToMessages);
      state.historyLoaded = true;
    },
    [LOAD_CHAT_HISTORY.rejected]: (state, action) => {
      state.historyLoaded = true;
      state.error = action.error.message;
    },
    [SUBMIT_RATING.fulfilled]: (state, action) => {
      const { entryId, rating, comment } = action.payload;
      const message = state.messages.find(m => m.entryId === entryId);
      if (message) message.rating = { rating, comment };
      state.ratingTarget = null;
    },
    [SUBMIT_RATING.rejected]: (state, action) => {
      state.error = action.error.message;
    },
  },
});

export const {
  OPEN_RATING_MODAL,
  CLOSE_RATING_MODAL,
  TOGGLE_FILTER_PANEL,
  SET_FILTER_PANEL_OPEN,
} = chat.actions;

export default chat.reducer;
